import React from 'react';
import { products } from '../lib/products';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import SEO from '../components/SEO';

export default function Products() {
  return (
    <div className="pt-20 min-h-screen bg-slate-50">
      <SEO 
        title="Products - STP, ETP, RO Plant, Boilers & Water Chemicals"
        description="Explore our range of water treatment products: STP, ETP, industrial RO systems, DM & softening plants, cooling towers, steam boilers, thermic fluid heaters and water treatment chemicals in Ludhiana, Punjab."
        keywords="water treatment products, STP plant, ETP plant, industrial RO system, DM plant, softening plant, cooling tower, steam boiler, thermic fluid heater, descaling chemicals, boiler chemicals, water testing kit"
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://bgtechnochem.com/" },
              { "@type": "ListItem", "position": 2, "name": "Products", "item": "https://bgtechnochem.com/products" }
            ]
          },
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Water Treatment Products by BG TECHNO CHEM",
            "itemListElement": products.flatMap((c) => c.items).map((item, i) => ({
              "@type": "ListItem",
              "position": i + 1,
              "item": {
                "@type": "Product",
                "name": item.name,
                "description": item.description,
                "image": item.image,
                "brand": { "@type": "Brand", "name": "BG TECHNO CHEM" },
                "offers": {
                  "@type": "Offer",
                  "availability": "https://schema.org/InStock",
                  "priceCurrency": "INR",
                  "price": "0",
                  "url": "https://bgtechnochem.com/products"
                }
              }
            }))
          }
        ]}
      />
      
      {/* Header */}
      <section className="bg-primary py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Badge className="mb-4 bg-white/20 hover:bg-white/30 text-white border-none px-4 py-1">
            Complete Range
          </Badge>
          <h1 className="text-4xl font-bold mb-6">Our Products</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            From industrial plants to specialized chemicals, we offer comprehensive water and energy solutions.
          </p>
        </div>
      </section>

      {/* Product Categories */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-24">
        {products.map((category, index) => {
          const Icon = category.icon;
          return (
            <section key={index} id={category.category.toLowerCase().replace(/\s+/g, '-')}>
              <div className="flex items-center space-x-4 mb-8">
                <div className="p-3 bg-primary/10 rounded-xl">
                  <Icon className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-3xl font-bold text-slate-900">{category.category}</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
                {category.items.map((item) => (
                  <Card key={item.id} className="group hover:shadow-xl transition-all duration-300 border-t-4 border-t-transparent hover:border-t-primary overflow-hidden">
                    <div className="flex flex-col h-full">
                      {/* Image Area */}
                      <div className="h-64 overflow-hidden relative bg-slate-100">
                        <img 
                          src={item.image} 
                          alt={item.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60"></div>
                        <div className="absolute bottom-4 left-4 right-4">
                          <h3 className="text-2xl font-bold text-white mb-1">{item.name}</h3>
                        </div>
                      </div>

                      <CardContent className="flex-grow p-6">
                        <p className="text-slate-600 mb-6 leading-relaxed">
                          {item.description}
                        </p>
                        
                        <div className="space-y-3 mb-8">
                          {item.specs.map((spec, i) => (
                            <div key={i} className="flex items-start">
                              <Check className="w-5 h-5 text-emerald-500 mr-3 flex-shrink-0 mt-0.5" />
                              <span className="text-sm font-medium text-slate-700">{spec}</span>
                            </div>
                          ))}
                        </div>

                        <div className="mt-auto">
                          <Link to="/contact">
                            <Button className="w-full bg-slate-900 hover:bg-primary transition-colors">
                              Request Quote <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                ))}
              </div>
              
              {index < products.length - 1 && (
                <div className="mt-24 border-b border-slate-200"></div>
              )}
            </section>
          );
        })}
      </div>

      {/* Bottom CTA */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Need a Custom Solution?</h2>
          <p className="text-slate-400 mb-8 text-lg">
            Our engineering team can design a system specifically for your industrial requirements.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-primary hover:bg-blue-600 text-lg px-8">
              Contact Engineering Team
            </Button>
          </Link>
        </div>
      </section>

    </div>
  );
}
